<!--
  FlatMenuSidebar — the sidebar shown when the user is NOT inside any
  cauldron (Home or any route without `meta.cauldron`).

  Acts as the orientation surface — a flat list of every top-level entry
  point so the user can dive into any cauldron from a single click. Once
  they navigate into a cauldron, the shell switches to the rail +
  contextual-sidebar layout.

  Visual contract: this menu MIRRORS the collapsed `CauldronRail`. Each
  row uses the same 64px (`w-16`) leading icon column as the rail's
  `w-16` width, with a 40px (`w-10 h-10`) emerald-tinted icon-button
  centred inside it. The divider beneath Home matches the rail's `w-7`
  horizontal rule at the same Y. The label is the only extra ink — the
  icons sit at the exact same x/y as in the rail so collapsing /
  expanding the sidebar doesn't shuffle them around.

  Order mirrors the rail's order:
    - Home
    - News (standalone, direct link — same as the rail's News button)
    - Forwardmarkten (Forward cauldron)
    - Flexmarkten (Flex cauldron)
    - Tools (Executive cauldron)
    - API (API cauldron)
    - Mijn omgeving (Personalisation cauldron)
    - Favorieten (Favourites cauldron, conditional on having any)
    - Platform — anchored to the bottom with `mt-auto`
-->
<template>
  <nav
    class="flat-menu flex flex-col h-full py-3 overflow-y-auto"
    aria-label="Primary navigation"
  >
    <flat-menu-row
      v-for="entry in topEntries"
      :key="entry.to"
      :to="entry.to"
      :label="t(entry.labelKey)"
      :icon="entry.icon"
      :active-match="entry.activeMatch"
      :disabled="demoEnabled && ['/my-environment/overview', '/favorites'].includes(entry.to)"
      @click="emit('navigate')"
    />

    <!-- Spacer + divider above the bottom-anchored Platform entry,
         mirroring the rail's bottom cluster. -->
    <div class="flex-1" aria-hidden="true" />
    <div class="flat-menu-divider" aria-hidden="true" />
    <flat-menu-row
      :disabled="demoEnabled"
      :to="platformEntry.to"
      :label="t(platformEntry.labelKey)"
      :icon="platformEntry.icon"
      :active-match="platformEntry.activeMatch"
      @click="emit('navigate')"
    />
  </nav>
</template>

<script setup>
  /* eslint-disable no-undef */
  import { demoEnabled } from '@/storage/demoMode';
  import FlatMenuRow from '@/components/navigation/FlatMenuRow.vue';
  import { useFavorites } from '@/composables/favorites';
  import {
    mdiAccountCircleOutline,
    mdiChartLine,
    mdiCogOutline,
    mdiHome,
    mdiLightningBolt,
    mdiNewspaperVariantOutline,
    mdiStarOutline,
    mdiStorefrontOutline,
    mdiToolboxOutline,
  } from '@mdi/js';
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';

  const emit = defineEmits(['navigate']);
  const { t } = useI18n();
  const { favorites } = useFavorites();

  const hasFavourites = computed(() => (favorites.value || []).length > 0);

  // All main entries — order matches the rail exactly. The bottom-
  // anchored Platform entry is rendered separately so the spacer +
  // divider can be inserted between the last entry and Platform.
  const topEntries = computed(() => {
    const entries = [
      {
        to: '/',
        labelKey: 'menu_home',
        icon: mdiHome,
        activeMatch: 'exact',
      },
      {
        to: '/news',
        labelKey: 'menu_news',
        icon: mdiNewspaperVariantOutline,
        activeMatch: 'prefix',
      },
      {
        to: '/reports/forward',
        labelKey: 'cauldron.forward.label',
        icon: mdiChartLine,
        activeMatch: 'prefix',
      },
      {
        to: '/reports/flexibility',
        labelKey: 'cauldron.flex.label',
        icon: mdiLightningBolt,
        activeMatch: 'prefix',
      },
      {
        to: '/tools',
        labelKey: 'menu_tools',
        icon: mdiToolboxOutline,
        activeMatch: 'prefix',
      },
      {
        to: '/api/store',
        labelKey: 'cauldron.api.label',
        icon: mdiStorefrontOutline,
        activeMatch: 'prefix',
      },
      {
        to: '/my-environment/overview',
        labelKey: 'sidebar.mijn_omgeving',
        icon: mdiAccountCircleOutline,
        activeMatch: 'prefix',
      },
    ];
    if (hasFavourites.value || demoEnabled.value) {
      entries.push({
        to: '/favorites',
        labelKey: 'cauldron.favourites.label',
        icon: mdiStarOutline,
        activeMatch: 'prefix',
      });
    }
    return entries;
  });

  // Bottom-anchored platform entry — settings/system surface. Matches the
  // "platform at the rail's foot" convention so its position stays
  // consistent whether the user is in flat-menu mode or rail+contextual
  // mode.
  const platformEntry = {
    to: '/profile',
    labelKey: 'cauldron.platform.label',
    icon: mdiCogOutline,
    activeMatch: 'prefix',
  };
</script>

<style scoped>
  /* Match the rail's divider exactly: 28px wide (`w-7`), centred in the
     64px icon column. The 18px left margin = `(64 - 28) / 2` so the
     divider's mid-point sits on the icons' vertical axis. */
  .flat-menu-divider {
    margin-top: 0.5rem;
    margin-bottom: 0.25rem;
    margin-left: 18px;
    width: 1.75rem;
    border-top: 1px solid rgb(var(--color-border-subtle)); /* gray-200 */
  }
  .dark .flat-menu-divider {
    border-top-color: rgb(var(--color-text-primary)); /* gray-800 */
  }
</style>
